// AdminOfferDetail — create + edit form for a single offer.
//
// Routed at /admin/offers/new (create) and /admin/offers/:id (edit).
// One component handles both — the URL param decides which.
//
// The form has three sections stacked top-to-bottom:
//   1. Identity   — title, description, type
//   2. Reward     — kind (cash|percent), value, optional cap
//   3. Lifecycle  — approval (auto|manual), starts_at, ends_at, per_user_limit, global_budget, status
//   4. Targets    — list of audience rows, with add/remove buttons (only on edit, after offer exists)
//
// The type-specific config (min_recharge for recharge_bonus, etc.)
// renders dynamically based on the chosen offer_type.

import { FormEvent, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AdminLayout from '@baaligames/shared-ui/admin/components/AdminLayout';
import DangerConfirmModal from '@baaligames/shared-ui/components/DangerConfirmModal';
import Icon from '@baaligames/shared-ui/components/Icon';
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';
import { ApiError } from '@baaligames/shared-ui/lib/api';
import OfferArtworkSection from '../components/OfferArtworkSection';
import AgentOptInsList from '../components/AgentOptInsList';
import OfferPerformanceTab from '../components/OfferPerformanceTab';
import { INPUT_CLS } from '../components/fieldStyles';

type OfferDetail = {
  id: number;
  title: string;
  description: string | null;
  /** Phase 14: artwork URL (640x640 transparent PNG). null if none. */
  image_url: string | null;
  offer_type: string;
  config: Record<string, unknown>;
  reward_kind: 'cash'|'percent'|'item';
  reward_value: number;
  reward_cap: number | null;
  approval: 'auto'|'manual';
  starts_at: string | null;
  ends_at: string | null;
  per_user_limit: number;
  global_budget: number | null;
  budget_used: number;
  status: 'draft'|'active'|'paused'|'archived';
};

type TargetRow = {
  id: number;
  user_id: number | null;
  state: string | null;
  district: string | null;
  segment: string | null;
  agent_id: number | null;
};

const TYPE_OPTIONS: Array<{ value: string; label: string; phase1: boolean }> = [
  { value: 'signup_bonus',       label: 'Signup Bonus',       phase1: true  },
  { value: 'daily_checkin',      label: 'Daily Check-in',     phase1: true  },
  { value: 'admin_announcement', label: 'Admin Announcement', phase1: true  },
  { value: 'recharge_bonus',     label: 'Recharge Bonus',     phase1: false }, // Phase 2 trigger
  { value: 'play_streak',        label: 'Play Streak',        phase1: false },
  { value: 'game_challenge',     label: 'Game Challenge',     phase1: false },
  { value: 'win_streak',         label: 'Win Streak',         phase1: false },
  { value: 'time_played',        label: 'Time Played',        phase1: false },
  { value: 'wallet_spend',       label: 'Wallet Spend',       phase1: false },
  { value: 'cashback',           label: 'Cashback',           phase1: false },
];

const SEGMENT_OPTIONS = ['new_users', 'big_spenders', 'inactive_30d'];

// Phase 16.0: per-type config defaults that satisfy the strict Zod
// schemas in adminOffers.controller.js. The form's render-time
// fallbacks (`?? 100`) only show those values; they don't write
// them to state. Without these defaults, saving an unedited form
// for any of the 6 strict-schema types posts `config: {}` and the
// backend rejects with "Validation error" + opaque issues.
//
// `game` for game_challenge is intentionally empty — admin must
// pick from the registry-driven dropdown. The Zod `string().min(1)`
// will catch the empty case and the new error display in `save()`
// surfaces it inline.
const DEFAULT_CONFIG_BY_TYPE: Record<string, Record<string, unknown>> = {
  signup_bonus:       {},
  daily_checkin:      {},
  admin_announcement: { message: '' },
  recharge_bonus:     { min_recharge: 100 },
  cashback:           { percent: 10 },
  win_streak:         { wins: 3, reward: 50 },
  game_challenge:     { min_stake: 50, count: 3, reward: 100 },
  play_streak:        { days: 7, min_matches_per_day: 1, reward: 50 },
  time_played:        { minutes: 30, reward: 25 },
  wallet_spend:       { threshold: 1000, window_days: 7, reward: 100 },
};

// Phase 16.1: REWARD_SOURCE_MAP — drives the dependent form layout.
//   'generic'         → show the standard "Reward" section (kind / value / cap).
//                       Used by types whose engine reads from offer.reward_value.
//   'recharge_formula'→ recharge_bonus only. Renders min_recharge + kind/value/cap
//                       inside ONE "Reward Formula" section so admin sees the
//                       "If recharge ≥ X then reward Y" formula together.
//   'config'          → Group B (engine reads from cfg.reward / cfg.percent).
//                       Generic Reward section is UNMOUNTED. The per-type
//                       config block is the only reward control, housed under
//                       a "Reward & Trigger Rules" section.
const REWARD_SOURCE_MAP: Record<string, 'generic' | 'recharge_formula' | 'config'> = {
  signup_bonus:       'generic',
  daily_checkin:      'generic',
  admin_announcement: 'generic',
  recharge_bonus:     'recharge_formula',
  cashback:           'config',
  win_streak:         'config',
  game_challenge:     'config',
  play_streak:        'config',
  time_played:        'config',
  wallet_spend:       'config',
};

// Stringify a Zod issues array into a friendly multi-line message.
// Backend returns errors like:
//   { code, message, details: { issues: [{ path: ['config','game'], message: 'Required' }, ...] } }
function formatValidationIssues(err: unknown): string | null {
  const e = err as { details?: { issues?: Array<{ path?: Array<string|number>; message?: string }> } };
  const issues = e?.details?.issues;
  if (!Array.isArray(issues) || issues.length === 0) return null;
  return issues
    .map((iss) => {
      const path = (iss.path || []).join('.');
      return path ? `${path}: ${iss.message}` : iss.message;
    })
    .join('\n');
}

export default function AdminOfferDetail() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'new';
  const offerId = isNew ? null : Number(id);
  const nav = useNavigate();

  const [form, setForm] = useState<Partial<OfferDetail>>({
    title: '',
    description: '',
    offer_type: 'signup_bonus',
    config: { ...DEFAULT_CONFIG_BY_TYPE.signup_bonus },
    reward_kind: 'cash',
    reward_value: 10,
    reward_cap: null,
    approval: 'auto',
    starts_at: null,
    ends_at: null,
    per_user_limit: 1,
    global_budget: null,
    status: 'draft',
  });
  const [targets, setTargets] = useState<TargetRow[]>([]);
  const [budgetUsed, setBudgetUsed] = useState(0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    if (isNew) return;
    (async () => {
      try {
        const r = await adminApi.get<{ offer: OfferDetail; targets: TargetRow[] }>(`/admin/offers/${offerId}`);
        setForm(r.offer);
        setTargets(r.targets);
        setBudgetUsed(r.offer.budget_used || 0);
      } catch (e) { setErr((e as ApiError).message); }
    })();
  }, [isNew, offerId]);

  // Phase 15.1: load the registry-driven game list once for the per-
  // type config game pickers. Falls back gracefully to an empty list
  // if the endpoint is slow / errors — admin can still type a key
  // freely if the picker is empty.
  const [games, setGames] = useState<Array<{ id: string; displayName?: string }>>([]);
  useEffect(() => {
    (async () => {
      try {
        const r = await adminApi.get<{ games: Array<{ id: string; displayName?: string }> }>(`/games`);
        setGames(r.games || []);
      } catch { /* leave empty — picker still usable as a free-text */ }
    })();
  }, []);

  // Phase 16.4 FIX: when offer_type is game_challenge, the config.game
  // field is REQUIRED by the backend Zod schema (game_challenge is the
  // one type where the challenge is inherently game-specific). The
  // GamePicker renders without an empty option in `required` mode, so
  // a fresh form starts with cfg.game="" while the dropdown VISUALLY
  // shows the first option. If the admin doesn't actively click the
  // picker, save() sends an empty `game` and the backend returns
  // "Invalid config for game_challenge: game Required".
  //
  // Snap config.game to the first available game whenever:
  //   - the type is game_challenge AND
  //   - games[] has loaded AND
  //   - the current config.game is empty.
  // This makes "open form, set type=game_challenge, click Save"
  // succeed without the admin having to remember to touch the picker.
  useEffect(() => {
    if (form.offer_type !== 'game_challenge') return;
    if (games.length === 0) return;
    const cfg = (form.config || {}) as Record<string, unknown>;
    if (cfg.game) return;
    setForm((f) => ({ ...f, config: { ...(f.config || {}), game: games[0].id } }));
    // We intentionally depend only on offer_type and games — config is
    // intentionally NOT in deps to avoid a self-fire loop after we set
    // game; the early-return on `cfg.game` is the loop guard for cases
    // where this effect re-fires.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.offer_type, games]);

  async function save(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      // 2026-05-20 — Client-side validation before submit, to catch
      // the engine's hard requirements early with a clear message
      // (vs. the admin saving a silently-broken offer that would
      // never credit at run time).
      //
      // Three rules:
      //   • win_streak       requires cfg.game (engine refuses
      //                      cross-game streaks)
      //   • time_played      requires cfg.game (engine refuses
      //                      cross-game time tracking)
      //   • game_challenge   requires cfg.min_stake ≥ 1 (engine
      //                      refuses min_stake=0 — that would let
      //                      every bet count, including ₹0 free
      //                      matches)
      const cfgPreCheck = (form.config || {}) as Record<string, unknown>;
      if (form.offer_type === 'win_streak' && !cfgPreCheck.game) {
        setErr('Pick a game — win-streak offers must be scoped to a single game.');
        setBusy(false);
        return;
      }
      if (form.offer_type === 'time_played' && !cfgPreCheck.game) {
        setErr('Pick a game — time-played offers must be scoped to a single game.');
        setBusy(false);
        return;
      }
      if (form.offer_type === 'game_challenge') {
        const ms = Number(cfgPreCheck.min_stake) | 0;
        if (ms < 1) {
          setErr('Minimum stake must be at least ₹1 — free matches cannot count toward a challenge.');
          setBusy(false);
          return;
        }
      }
      // Phase 16.1: silent reward_kind/value defaults for Group B types.
      // The generic Reward section is unmounted for these types; the
      // config block carries the real reward number. We mirror that
      // value into reward_value so admin queries / list panels that
      // read those fields show meaningful numbers.
      const cfg = (form.config || {}) as Record<string, unknown>;
      const rewardSource = REWARD_SOURCE_MAP[form.offer_type as string] || 'generic';
      let rewardKind = form.reward_kind;
      let rewardValue = Number(form.reward_value) || 0;
      let rewardCap = form.reward_cap ? Number(form.reward_cap) : null;
      if (rewardSource === 'config') {
        if (form.offer_type === 'cashback') {
          // cashback engine reads cfg.percent. Mirror as percent + cap.
          rewardKind = 'percent';
          rewardValue = Number(cfg.percent) || 0;
          rewardCap = cfg.daily_cap ? Number(cfg.daily_cap) : null;
        } else {
          // win_streak / game_challenge / play_streak / time_played /
          // wallet_spend all credit cfg.reward as a flat ₹ amount.
          rewardKind = 'cash';
          rewardValue = Number(cfg.reward) || 0;
          rewardCap = null;
        }
      }
      // Phase 16.4 FIX: convert the form's datetime strings (which are
      // built by appending ":00.000Z" to a <input type="datetime-local">
      // value) into MySQL DATETIME format `YYYY-MM-DD HH:MM:SS` before
      // sending. The legacy ISO-with-Z format MySQL was rejecting at
      // INSERT time with "Incorrect datetime value" — surfaced to the
      // admin as a generic "Something went wrong" 500. Ditch the bogus
      // Z (the value was never UTC anyway — it was the admin's local
      // time mislabelled) and send a clean MySQL string.
      const toMySqlDateTime = (s: string | null | undefined): string | null => {
        if (!s) return null;
        // Accept either 'YYYY-MM-DDTHH:mm[:ss[.sss[Z]]]' (frontend) or
        // 'YYYY-MM-DD HH:MM:SS' (already-formatted, e.g. when editing).
        const cleaned = s.replace('T', ' ').replace(/\.\d+/, '').replace(/Z$/, '').trim();
        // Pad missing seconds: '2026-05-02 04:32' -> '2026-05-02 04:32:00'
        return /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/.test(cleaned) ? cleaned + ':00' : cleaned;
      };

      const payload = {
        title: form.title,
        description: form.description || null,
        offer_type: form.offer_type,
        config: form.config || {},
        reward_kind: rewardKind,
        reward_value: rewardValue,
        reward_cap: rewardCap,
        approval: form.approval,
        starts_at: toMySqlDateTime(form.starts_at),
        ends_at: toMySqlDateTime(form.ends_at),
        per_user_limit: Number(form.per_user_limit) || 1,
        global_budget: form.global_budget ? Number(form.global_budget) : null,
        status: form.status,
      };
      if (isNew) {
        const r = await adminApi.post<{ offer: OfferDetail }>('/admin/offers', { ...payload, targets: [] });
        nav(`/admin/offers/${r.offer.id}`, { replace: true });
        setToast('Offer created — add targets below to restrict the audience');
      } else {
        await adminApi.patch(`/admin/offers/${offerId}`, payload);
        setToast('Saved');
      }
    } catch (e) {
      // Phase 16.0: surface backend Zod issues so admin can see WHICH
      // field broke instead of an opaque "Validation error".
      const apiErr = e as ApiError;
      const issues = formatValidationIssues(apiErr);
      setErr(issues
        ? `${apiErr.message}\n${issues}`
        : apiErr.message);
    } finally { setBusy(false); }
  }

  // Type-specific config widgets — kept in this component so the
  // controller and the UI agree on the config shape.
  //
  // Phase 15.1: every offer type the engine supports now has a real
  // form. Five of them (win_streak / game_challenge / play_streak /
  // time_played / wallet_spend) credit from cfg.reward and ignore
  // offer.reward_value entirely (legacy engine behaviour). For
  // those the form renders an inline note clarifying that the
  // Reward section above is ignored — the cfg.reward field below
  // is what credits the player.
  function ConfigFields() {
    const cfg = (form.config || {}) as Record<string, unknown>;
    const setCfg = (next: Record<string, unknown>) =>
      setForm({ ...form, config: { ...cfg, ...next } });

    switch (form.offer_type) {
      // signup_bonus + daily_checkin have NO extra config — the engine
      // reads only offer.reward_value. Return null so the section
      // wrapper doesn't render an empty row.
      case 'signup_bonus':
      case 'daily_checkin':
        return null;

      case 'admin_announcement':
        return (
          <Field label="Message shown to player">
            <textarea
              rows={2} className={inputCls + ' resize-y'}
              maxLength={500}
              value={(cfg.message as string) ?? ''}
              onChange={(e) => setCfg({ message: e.target.value })}
            />
          </Field>
        );

      case 'recharge_bonus':
        return (
          <Field label="Minimum recharge to qualify (₹)" hint="Player must recharge at least this amount before the bonus fires.">
            <input
              type="number" min={1} className={inputCls}
              value={(cfg.min_recharge as number) ?? 100}
              onChange={(e) => setCfg({ min_recharge: Number(e.target.value) || 0 })}
            />
          </Field>
        );

      case 'cashback':
        return (
          <>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Cashback percent of stake (1-100)" required hint="Player gets this % of their losing bet back.">
                <input
                  type="number" min={1} max={100} className={inputCls}
                  value={(cfg.percent as number) ?? 10}
                  onChange={(e) => setCfg({ percent: Number(e.target.value) || 0 })}
                />
              </Field>
              <Field label="Daily cap per player (₹)" hint="Optional. Maximum cashback the player can earn in a single day across all losses.">
                <input
                  type="number" min={0} className={inputCls}
                  value={(cfg.daily_cap as number) ?? ''}
                  onChange={(e) => setCfg({ daily_cap: e.target.value ? Number(e.target.value) : undefined })}
                />
              </Field>
            </div>
            <GamePicker
              value={(cfg.game as string) ?? ''}
              onChange={(v) => setCfg({ game: v || undefined })}
              games={games}
              labelHint="Restrict to one game (optional — leave 'Any' for all games)"
            />
          </>
        );

      case 'win_streak':
        return (
          <>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Consecutive wins required" required>
                <input
                  type="number" min={1} className={inputCls}
                  value={(cfg.wins as number) ?? 3}
                  onChange={(e) => setCfg({ wins: Number(e.target.value) || 1 })}
                />
              </Field>
              <Field label="Reward credited at streak hit (₹)" required>
                <input
                  type="number" min={0} className={inputCls}
                  value={(cfg.reward as number) ?? 50}
                  onChange={(e) => setCfg({ reward: Number(e.target.value) || 0 })}
                />
              </Field>
            </div>
            {/* 2026-05-20 — game is now REQUIRED. Cross-game streaks
                are not supported by the engine (would silently never
                credit). The picker no longer offers the "Any game"
                empty option in `required` mode and the engine returns
                0 if cfg.game is missing. */}
            <GamePicker
              value={(cfg.game as string) ?? ''}
              onChange={(v) => setCfg({ game: v })}
              games={games}
              required
              labelHint="The streak is game-specific — pick one"
            />
          </>
        );

      case 'game_challenge':
        return (
          <>
            <GamePicker
              value={(cfg.game as string) ?? ''}
              onChange={(v) => setCfg({ game: v })}
              games={games}
              required
              labelHint="The challenge IS game-specific — pick one"
            />
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {/* 2026-05-20 — min={1} (was min={0}). A min_stake of 0
                  let every bet count toward the challenge, including
                  the negative-amount filter degenerating into "≤ 0"
                  which matches every bet row. Free matches no longer
                  count: stake must be strictly positive. Engine also
                  rejects cfg.min_stake < 1 at compute time. */}
              <Field label="Min stake per match (₹)" required hint="Must be ≥ 1 — a stake of 0 is rejected by the engine">
                <input
                  type="number" min={1} className={inputCls}
                  value={(cfg.min_stake as number) ?? 50}
                  onChange={(e) => setCfg({ min_stake: Math.max(1, Number(e.target.value) || 1) })}
                />
              </Field>
              <Field label="Match count required" required>
                <input
                  type="number" min={1} className={inputCls}
                  value={(cfg.count as number) ?? 3}
                  onChange={(e) => setCfg({ count: Number(e.target.value) || 1 })}
                />
              </Field>
              {/* 2026-05-20: window_days now honoured by the engine
                  (previously hardcoded to 30). Default 7 — matches
                  the most common product framing of a weekly challenge. */}
              <Field label="Window (days)" required hint="How far back the engine looks when counting qualifying matches">
                <input
                  type="number" min={1} max={365} className={inputCls}
                  value={(cfg.window_days as number) ?? 7}
                  onChange={(e) => setCfg({ window_days: Number(e.target.value) || 1 })}
                />
              </Field>
              <Field label="Reward credited at challenge complete (₹)" required>
                <input
                  type="number" min={0} className={inputCls}
                  value={(cfg.reward as number) ?? 100}
                  onChange={(e) => setCfg({ reward: Number(e.target.value) || 0 })}
                />
              </Field>
            </div>
          </>
        );

      case 'play_streak':
        return (
          <>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Consecutive days" required>
                <input
                  type="number" min={1} className={inputCls}
                  value={(cfg.days as number) ?? 7}
                  onChange={(e) => setCfg({ days: Number(e.target.value) || 1 })}
                />
              </Field>
              <Field label="Min matches per day" required>
                <input
                  type="number" min={1} className={inputCls}
                  value={(cfg.min_matches_per_day as number) ?? 1}
                  onChange={(e) => setCfg({ min_matches_per_day: Number(e.target.value) || 1 })}
                />
              </Field>
              <Field label="Reward credited at streak hit (₹)" required>
                <input
                  type="number" min={0} className={inputCls}
                  value={(cfg.reward as number) ?? 50}
                  onChange={(e) => setCfg({ reward: Number(e.target.value) || 0 })}
                />
              </Field>
            </div>
          </>
        );

      case 'time_played':
        return (
          <>
            {/* 2026-05-20 — game is REQUIRED. Without it, a "play 30
                min carrom" offer would silently credit on a 30-min
                ludo session. The engine now refuses to credit if
                cfg.game is missing. */}
            <GamePicker
              value={(cfg.game as string) ?? ''}
              onChange={(v) => setCfg({ game: v })}
              games={games}
              required
              labelHint="The session must be in this specific game — pick one"
            />
            <div className="grid grid-cols-2 gap-3">
              <Field label="Minutes played in a single session" required>
                <input
                  type="number" min={1} className={inputCls}
                  value={(cfg.minutes as number) ?? 30}
                  onChange={(e) => setCfg({ minutes: Number(e.target.value) || 1 })}
                />
              </Field>
              <Field label="Reward credited at session end (₹)" required>
                <input
                  type="number" min={0} className={inputCls}
                  value={(cfg.reward as number) ?? 25}
                  onChange={(e) => setCfg({ reward: Number(e.target.value) || 0 })}
                />
              </Field>
            </div>
          </>
        );

      case 'wallet_spend':
        return (
          <>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Total spend threshold (₹)" required>
                <input
                  type="number" min={1} className={inputCls}
                  value={(cfg.threshold as number) ?? 1000}
                  onChange={(e) => setCfg({ threshold: Number(e.target.value) || 1 })}
                />
              </Field>
              <Field label="Window (days)" required>
                <input
                  type="number" min={1} className={inputCls}
                  value={(cfg.window_days as number) ?? 7}
                  onChange={(e) => setCfg({ window_days: Number(e.target.value) || 1 })}
                />
              </Field>
              <Field label="Reward credited at threshold (₹)" required>
                <input
                  type="number" min={0} className={inputCls}
                  value={(cfg.reward as number) ?? 100}
                  onChange={(e) => setCfg({ reward: Number(e.target.value) || 0 })}
                />
              </Field>
            </div>
          </>
        );

      default:
        return (
          <p className="text-xs text-amber-700 dark:text-amber-300/80
                        bg-amber-500/5 ring-1 ring-inset ring-amber-500/20 rounded-lg px-3 py-2">
            Unknown offer type — please update the form.
          </p>
        );
    }
  }

  // ──── Target management (edit only) ────

  const [newTarget, setNewTarget] = useState<{ kind: 'user_id'|'state'|'district'|'segment'; value: string }>({
    kind: 'state', value: '',
  });

  async function addTarget() {
    if (isNew || !newTarget.value.trim()) return;
    try {
      const body: Record<string, unknown> = {};
      if (newTarget.kind === 'user_id') body.user_id = Number(newTarget.value);
      else if (newTarget.kind === 'state') body.state = newTarget.value.trim();
      else if (newTarget.kind === 'district') body.district = newTarget.value.trim();
      else if (newTarget.kind === 'segment') body.segment = newTarget.value;
      await adminApi.post(`/admin/offers/${offerId}/targets`, body);
      // refresh
      const r = await adminApi.get<{ targets: TargetRow[] }>(`/admin/offers/${offerId}`);
      setTargets(r.targets);
      setNewTarget({ kind: 'state', value: '' });
      setToast('Target added');
    } catch (e) { setErr((e as ApiError).message); }
  }

  async function removeTarget(targetId: number) {
    if (isNew) return;
    try {
      await adminApi.del(`/admin/offers/${offerId}/targets/${targetId}`);
      const r = await adminApi.get<{ targets: TargetRow[] }>(`/admin/offers/${offerId}`);
      setTargets(r.targets);
    } catch (e) { setErr((e as ApiError).message); }
  }

  // ─── Batch 3 #19: Quick Actions ──────────────────────────────────────
  // Three top-right actions on existing offers: Activate/Pause,
  // Duplicate, Delete. None render in the create flow (offerId is null).
  const [quickBusy, setQuickBusy] = useState<'toggle' | 'duplicate' | 'delete' | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  /** Activate ↔ Pause one-click toggle. Active offers go to paused;
   *  draft/paused/archived all go to active so the admin can use this
   *  as a one-click "publish" too. Status edit goes through the same
   *  PATCH endpoint the form uses. */
  async function toggleActivePause() {
    if (isNew || !offerId) return;
    const next: OfferDetail['status'] = form.status === 'active' ? 'paused' : 'active';
    setQuickBusy('toggle'); setErr(null);
    try {
      await adminApi.patch(`/admin/offers/${offerId}`, { status: next });
      setForm((f) => ({ ...f, status: next }));
      setToast(next === 'active' ? 'Offer activated — players will see it now.' : 'Offer paused — hidden from players.');
      setTimeout(() => setToast(null), 4000);
    } catch (e) { setErr((e as ApiError).message); }
    finally { setQuickBusy(null); }
  }

  /** Client-side duplicate — read the current form state, POST a new
   *  offer with the same fields, navigate to the new offer's edit
   *  page. Targets and opt-ins are NOT cloned (admin re-adds them) —
   *  same convention as cloning a doc-template. */
  async function duplicateOffer() {
    if (isNew) return;
    setQuickBusy('duplicate'); setErr(null);
    try {
      const cloned = {
        title:           `Copy of ${form.title || 'Untitled'}`.slice(0, 120),
        description:     form.description || '',
        offer_type:      form.offer_type,
        config:          form.config || {},
        reward_kind:     form.reward_kind,
        reward_value:    form.reward_value,
        reward_cap:      form.reward_cap,
        approval:        form.approval,
        starts_at:       null,           // fresh date window
        ends_at:         null,
        per_user_limit:  form.per_user_limit,
        global_budget:   form.global_budget,
        status:          'draft' as const,  // duplicated offers always start as draft
        targets:         [],
      };
      const r = await adminApi.post<{ offer: OfferDetail }>('/admin/offers', cloned);
      nav(`/rewards/offers/${r.offer.id}`, { replace: false });
    } catch (e) { setErr((e as ApiError).message); }
    finally { setQuickBusy(null); }
  }

  /** Hard-delete — the actual API call, fired from inside the
   *  DangerConfirmModal. Throws on error so the modal stays open and
   *  shows the message inline. */
  async function deleteOffer() {
    if (isNew || !offerId) return;
    setQuickBusy('delete');
    try {
      await adminApi.del(`/admin/offers/${offerId}`);
      setConfirmDelete(false);
      nav('/rewards/offers', { replace: true });
    } catch (e) {
      setQuickBusy(null);
      throw e;  // bubble to DangerConfirmModal
    }
  }

  return (
    <AdminLayout
      title={isNew ? 'New offer' : `Edit: ${form.title || 'Untitled'}`}
      titleIcon="card_giftcard"
      subtitle={isNew ? 'Configure a new reward' : `Offer #${offerId}`}
    >
      {err && <div className="mb-3 rounded-xl bg-rose-50 dark:bg-rose-900/30 ring-1 ring-inset ring-rose-200/60 dark:ring-rose-800/50 text-rose-700 dark:text-rose-200 px-4 py-3 text-sm whitespace-pre-line">{err}</div>}
      {toast && <div className="mb-3 rounded-xl bg-emerald-50 dark:bg-emerald-500/10 ring-1 ring-inset ring-emerald-200/60 dark:ring-emerald-400/30 text-emerald-700 dark:text-emerald-200 px-4 py-3 text-sm">{toast}</div>}

      {/* Batch 3 #19: Quick Actions strip — Activate/Pause toggle,
          Duplicate, Delete. Only renders for existing offers (the
          create flow has nothing to act on yet). Aligned right and
          spaced from the form below. */}
      {!isNew && (
        <div className="mb-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="text-[11px] uppercase tracking-wider font-bold text-arena-600 dark:text-neon-200/55">
            Quick actions
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Activate/Pause toggle — emerald when activating (the
                positive action), amber when pausing (the gentler
                destructive). One-click; no confirm modal because it's
                fully reversible. */}
            <button
              type="button"
              onClick={toggleActivePause}
              disabled={quickBusy !== null}
              className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-[12px] font-bold text-white transition active:scale-95 disabled:opacity-50 ${
                form.status === 'active'
                  ? 'bg-amber-600 hover:bg-amber-500 shadow-[0_2px_10px_rgba(217,119,6,0.35)]'
                  : 'bg-emerald-600 hover:bg-emerald-500 shadow-[0_2px_10px_rgba(16,185,129,0.35)]'
              }`}
            >
              {quickBusy === 'toggle'
                ? <Icon name="progress_activity" size={13} className="animate-spin" />
                : <Icon name={form.status === 'active' ? 'pause' : 'play_arrow'} size={13} />}
              {form.status === 'active' ? 'Pause' : 'Activate'}
            </button>

            {/* Duplicate — clones the current offer's fields into a new
                draft and navigates to the new offer's edit page. */}
            <button
              type="button"
              onClick={duplicateOffer}
              disabled={quickBusy !== null}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-[12px] font-semibold
                         bg-violet-600 hover:bg-violet-500 text-white
                         shadow-[0_2px_10px_rgba(139,92,246,0.35)]
                         transition active:scale-95 disabled:opacity-50"
            >
              {quickBusy === 'duplicate'
                ? <Icon name="progress_activity" size={13} className="animate-spin" />
                : <Icon name="content_copy" size={13} />}
              Duplicate
            </button>

            {/* Delete — opens the DangerConfirmModal; the actual call
                lives inside deleteOffer(). Backend refuses if there
                are credited claims. */}
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              disabled={quickBusy !== null}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-[12px] font-bold text-white transition active:scale-95 disabled:opacity-50
                         bg-rose-600 hover:bg-rose-500 shadow-[0_2px_10px_rgba(244,63,94,0.35)]"
            >
              <Icon name="delete" size={13} /> Delete
            </button>
          </div>
        </div>
      )}

      {confirmDelete && (
        <DangerConfirmModal
          open={true}
          onClose={() => setConfirmDelete(false)}
          onConfirm={deleteOffer}
          title={`Delete offer "${form.title || 'Untitled'}"?`}
          description={
            <>
              Hard-deletes the offer + cascades targets, opt-ins, and pending/rejected claims in one
              transaction. The backend will REFUSE if any <strong>credited</strong> claims exist (their wallet-ledger
              references must stay intact) — in that case set status to <code className="font-mono">archived</code> instead.
              This cannot be undone.
            </>
          }
          confirmLabel="Delete"
          cancelLabel="Cancel"
          confirmWord="DELETE"
        />
      )}

      <form onSubmit={save} className="space-y-6">
        {/* Identity — title, description, type only. ConfigFields
            moved out into a dedicated reward-context section below
            (Phase 16.1 — truly dependent layout). */}
        <Section title="Identity" icon="info">
          <Field label="Title" required>
            <input className={inputCls} value={form.title || ''} required maxLength={120}
              onChange={(e) => setForm({ ...form, title: e.target.value })} />
          </Field>
          <Field label="Description (shown to player)">
            <textarea rows={2} className={inputCls + ' resize-y'} maxLength={500}
              value={form.description || ''}
              onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </Field>
          <Field label="Type">
            <select className={inputCls} value={form.offer_type}
              onChange={(e) => {
                const newType = e.target.value;
                setForm({ ...form, offer_type: newType,
                          config: { ...(DEFAULT_CONFIG_BY_TYPE[newType] || {}) } });
              }}>
              {TYPE_OPTIONS.map((t) =>
                <option key={t.value} value={t.value}>
                  {t.label}{!t.phase1 ? ' (deferred)' : ''}
                </option>
              )}
            </select>
          </Field>
        </Section>

        {/* Artwork (Phase 14) — only available after the offer is
            saved, since the upload route needs a real id. On the
            create flow we render an inline hint so the admin knows
            to come back after Save. */}
        <Section title="Artwork" icon="image">
          {isNew ? (
            <div className="rounded-lg bg-amber-500/5 ring-1 ring-inset ring-amber-500/30
                            text-amber-700 dark:text-amber-300 text-xs px-3 py-2 inline-flex items-center gap-1.5">
              <Icon name="info" size={14} />
              Save the offer first, then upload artwork on this page.
              The card &amp; hero will fall back to the type-icon until you upload.
            </div>
          ) : (
            <OfferArtworkSection
              offerId={offerId!}
              imageUrl={form.image_url ?? null}
              onChange={(newUrl) => setForm({ ...form, image_url: newUrl })}
            />
          )}
        </Section>

        {/* Phase 16.1 — dependent reward section.
            Renders ONE of three layouts based on REWARD_SOURCE_MAP:
              'generic'         → standard Reward section (kind/value/cap)
                                  + ConfigFields below for any extras
                                  (e.g. admin_announcement message).
              'recharge_formula'→ recharge_bonus only — min_recharge +
                                  kind/value/cap visually grouped to
                                  express "If recharge ≥ X, then Y".
              'config'          → Group B types — no generic Reward
                                  section. The per-type ConfigFields IS
                                  the reward control, housed under
                                  "Reward & Trigger Rules".
        */}
        {REWARD_SOURCE_MAP[form.offer_type as string] === 'generic' && (
          <Section title="Reward" icon="redeem">
            <Field label="Reward kind">
              <select className={inputCls} value={form.reward_kind}
                onChange={(e) => setForm({ ...form, reward_kind: e.target.value as 'cash'|'percent' })}>
                <option value="cash">Cash (flat ₹ amount)</option>
                <option value="percent">Percent (of recharge / win)</option>
              </select>
            </Field>
            <Field label={form.reward_kind === 'percent' ? 'Percent (1-100)' : 'Amount (₹)'}>
              <input type="number" min={0} className={inputCls} value={form.reward_value || 0}
                onChange={(e) => setForm({ ...form, reward_value: Number(e.target.value) || 0 })} />
            </Field>
            {form.reward_kind === 'percent' && (
              <Field label="Cap (₹) — optional">
                <input type="number" min={0} className={inputCls} value={form.reward_cap || ''}
                  onChange={(e) => setForm({ ...form, reward_cap: e.target.value ? Number(e.target.value) : null })} />
              </Field>
            )}
            {/* Extras for Group A types that need them (e.g. admin_announcement message). */}
            <ConfigFields />
          </Section>
        )}

        {REWARD_SOURCE_MAP[form.offer_type as string] === 'recharge_formula' && (
          <Section title="Reward Formula" icon="redeem">
            <p className="text-[11.5px] text-arena-600 dark:text-neon-200/55 -mt-1 mb-2 leading-snug">
              <strong>If</strong> the player recharges at least the minimum below,{' '}
              <strong>then</strong> they get the reward defined to the right.
            </p>
            <ConfigFields />
            <Field label="Reward kind">
              <select className={inputCls} value={form.reward_kind}
                onChange={(e) => setForm({ ...form, reward_kind: e.target.value as 'cash'|'percent' })}>
                <option value="cash">Cash (flat ₹ amount)</option>
                <option value="percent">Percent (of recharge)</option>
              </select>
            </Field>
            <Field label={form.reward_kind === 'percent' ? 'Percent (1-100)' : 'Amount (₹)'}>
              <input type="number" min={0} className={inputCls} value={form.reward_value || 0}
                onChange={(e) => setForm({ ...form, reward_value: Number(e.target.value) || 0 })} />
            </Field>
            {form.reward_kind === 'percent' && (
              <Field label="Cap (₹) — optional"
                hint="Maximum reward in rupees if the percent calculation exceeds this.">
                <input type="number" min={0} className={inputCls} value={form.reward_cap || ''}
                  onChange={(e) => setForm({ ...form, reward_cap: e.target.value ? Number(e.target.value) : null })} />
              </Field>
            )}
          </Section>
        )}

        {REWARD_SOURCE_MAP[form.offer_type as string] === 'config' && (
          <Section title="Reward & Trigger Rules" icon="redeem">
            <p className="text-[11.5px] text-arena-600 dark:text-neon-200/55 -mt-1 mb-2 leading-snug">
              The trigger conditions and the reward both live here for this offer
              type. The generic Reward section is intentionally hidden — the engine
              credits the value below directly when conditions are met.
            </p>
            <ConfigFields />
          </Section>
        )}

        {/* Lifecycle */}
        <Section title="Lifecycle" icon="schedule">
          <Field label="Approval">
            <select className={inputCls} value={form.approval}
              onChange={(e) => setForm({ ...form, approval: e.target.value as 'auto'|'manual' })}>
              <option value="auto">Auto-credit (no admin step)</option>
              <option value="manual">Manual review (lands in queue)</option>
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Starts at (optional)">
              <input type="datetime-local" className={inputCls} value={form.starts_at?.slice(0, 16) || ''}
                onChange={(e) => setForm({ ...form, starts_at: e.target.value ? e.target.value + ':00.000Z' : null })} />
            </Field>
            <Field label="Ends at (optional)">
              <input type="datetime-local" className={inputCls} value={form.ends_at?.slice(0, 16) || ''}
                onChange={(e) => setForm({ ...form, ends_at: e.target.value ? e.target.value + ':00.000Z' : null })} />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Per-user limit (0 = unlimited)">
              <input type="number" min={0} className={inputCls} value={form.per_user_limit ?? 1}
                onChange={(e) => setForm({ ...form, per_user_limit: Number(e.target.value) || 0 })} />
            </Field>
            <Field label="Global budget (₹) — optional">
              <input type="number" min={0} className={inputCls} value={form.global_budget || ''}
                onChange={(e) => setForm({ ...form, global_budget: e.target.value ? Number(e.target.value) : null })} />
            </Field>
          </div>
          {!isNew && form.global_budget != null && (
            <p className="text-xs text-arena-400 dark:text-neon-200/70">
              Used so far: <b className="tabular-nums">₹{budgetUsed.toLocaleString('en-IN')}</b> of ₹{form.global_budget.toLocaleString('en-IN')}
            </p>
          )}
          <Field label="Status">
            <select className={inputCls} value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value as OfferDetail['status'] })}>
              <option value="draft">Draft (hidden, not firing)</option>
              <option value="active">Active (live)</option>
              <option value="paused">Paused (visible but not firing)</option>
              <option value="archived">Archived (gone)</option>
            </select>
          </Field>
        </Section>

        <div className="flex gap-2 justify-end">
          <button type="button" onClick={() => nav('/offers')}
            className="px-4 py-2 rounded-xl bg-white/5 border ring-white/10 text-sm hover:bg-white/10">
            Cancel
          </button>
          <button type="submit" disabled={busy}
            className="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-bold disabled:opacity-50 inline-flex items-center gap-1">
            {busy ? <Icon name="progress_activity" size={16} className="animate-spin" /> : <Icon name="save" size={16} />}
            {isNew ? 'Create offer' : 'Save changes'}
          </button>
        </div>
      </form>

      {/* Targets — shown only after the offer exists */}
      {!isNew && (
        <Section title="Audience" icon="groups">
          <p className="text-xs text-arena-400 dark:text-neon-200/70 mb-3">
            No rows = visible to <b>everyone</b>. Add one or more rows to restrict.
            Multiple rows = OR (matches any).
          </p>
          <div className="rounded-xl bg-arena-50/40 dark:bg-arena-800/40 ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40 overflow-hidden mb-3">
            {targets.length === 0 ? (
              <div className="px-4 py-3 text-sm text-arena-600 dark:text-neon-200/60">No targets — visible to everyone.</div>
            ) : (
              <table className="w-full text-sm">
                <tbody>
                  {targets.map((t) => (
                    <tr key={t.id} className="border-t ring-arena-200/60 dark:ring-arena-600/40 first:border-t-0">
                      <td className="px-4 py-2 text-arena-700 dark:text-neon-200/75">
                        {t.user_id     && <>User #{t.user_id}</>}
                        {t.state       && <>State: <b>{t.state}</b></>}
                        {t.district    && <>District: <b>{t.district}</b></>}
                        {t.segment     && <>Segment: <b>{t.segment}</b></>}
                        {t.agent_id && <>Agent #{t.agent_id}</>}
                      </td>
                      <td className="px-4 py-2 text-right">
                        <button onClick={() => removeTarget(t.id)}
                          className="text-xs px-2 py-1 rounded-full bg-rose-50 dark:bg-rose-900/30 ring-1 ring-inset ring-rose-200/60 dark:ring-rose-800/50 text-rose-700 dark:text-rose-300 hover:bg-rose-100 dark:bg-rose-900/50">
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          {/* Add target form */}
          <div className="flex flex-wrap gap-2 items-end">
            <Field label="Type">
              <select className={inputCls + ' w-32'} value={newTarget.kind}
                onChange={(e) => setNewTarget({ kind: e.target.value as 'user_id'|'state'|'district'|'segment', value: '' })}>
                <option value="state">State</option>
                <option value="district">District</option>
                <option value="user_id">User ID</option>
                <option value="segment">Segment</option>
              </select>
            </Field>
            <Field label="Value">
              {newTarget.kind === 'segment' ? (
                <select className={inputCls + ' w-44'} value={newTarget.value}
                  onChange={(e) => setNewTarget({ ...newTarget, value: e.target.value })}>
                  <option value="">Pick…</option>
                  {SEGMENT_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              ) : (
                <input
                  type={newTarget.kind === 'user_id' ? 'number' : 'text'}
                  className={inputCls + ' w-44'}
                  value={newTarget.value}
                  onChange={(e) => setNewTarget({ ...newTarget, value: e.target.value })}
                  placeholder={newTarget.kind === 'user_id' ? '123' : 'Maharashtra'}
                />
              )}
            </Field>
            <button type="button" onClick={addTarget}
              className="px-3 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-bold inline-flex items-center gap-1">
              <Icon name="add" size={16} /> Add
            </button>
          </div>
        </Section>
      )}

      {/* Phase 15.6 — agent opt-ins + per-offer rate override.
          Only meaningful after the offer exists. */}
      {!isNew && offerId && (
        <Section title="Agent opt-ins" icon="hub">
          <AgentOptInsList offerId={offerId} />
        </Section>
      )}

      {/* Phase 15.7 — per-offer performance analytics. */}
      {!isNew && offerId && (
        <Section title="Performance" icon="bar_chart">
          <OfferPerformanceTab offerId={offerId} />
        </Section>
      )}
    </AdminLayout>
  );
}

// ─── Tiny presentational helpers ───

// 2026-05-20 — was a local clone of the canonical INPUT_CLS that
// drifted from the source-of-truth. Aliased to the shared constant
// so the dark-mode fill + ring fix in components/fieldStyles.ts
// applies here automatically. All 16 in-file references untouched.
const inputCls = INPUT_CLS;

function Section({ title, icon, children }: { title: string; icon: string; children: React.ReactNode }) {
  return (
    <section className="rounded-2xl bg-white dark:bg-arena-800/60 ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40 p-4 space-y-3">
      <h3 className="font-bold text-arena-800 dark:text-neon-200/90 inline-flex items-center gap-2">
        <Icon name={icon} size={18} /> {title}
      </h3>
      {children}
    </section>
  );
}

function Field({ label, required, hint, children }: {
  label: string; required?: boolean; hint?: string; children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-wider text-arena-400 dark:text-neon-200/70 mb-1">
        {label}{required && <span className="text-rose-400 ml-1">*</span>}
      </span>
      {children}
      {hint && (
        <span className="block text-[11px] text-arena-600 dark:text-neon-200/60 mt-1 leading-snug">
          {hint}
        </span>
      )}
    </label>
  );
}

// Phase 15.1 — game picker. Reads the registry-driven games list
// (passed in as a prop so we don't refetch per render). Renders a
// select with an "Any game" empty option unless `required` is set.
function GamePicker({
  value, onChange, games, required, labelHint,
}: {
  value: string;
  onChange: (v: string) => void;
  games: Array<{ id: string; displayName?: string }>;
  required?: boolean;
  labelHint?: string;
}) {
  return (
    <Field label={`Game${required ? '' : ' (optional)'}`} required={required}>
      <select className={inputCls} value={value} onChange={(e) => onChange(e.target.value)}>
        {!required && <option value="">— Any game —</option>}
        {games.map((g) =>
          <option key={g.id} value={g.id}>{g.displayName || g.id}</option>
        )}
      </select>
      {labelHint && (
        <span className="block text-[11px] text-arena-600 dark:text-neon-200/60 mt-1">{labelHint}</span>
      )}
    </Field>
  );
}

// Phase 16.1: ConfigRewardOverridesNote removed. The note existed
// because Phase 15.1 left the redundant generic Reward section visible
// for Group B types. Now that section is fully unmounted (see
// REWARD_SOURCE_MAP), the warning is unnecessary.
